import Vue from 'vue'
import VueI18n from 'vue-i18n'
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  LOCALE_COOKIE_KEY,
  normalizeLocale,
} from '~/utils/locale'

Vue.use(VueI18n)

const loadedLanguages = ['en']

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const mergeLocaleMessages = (fallback, locale) => {
  if (!isPlainObject(fallback)) {
    return locale
  }
  if (!isPlainObject(locale)) {
    return { ...fallback }
  }

  const merged = { ...fallback }
  Object.keys(locale).forEach((key) => {
    const localeValue = locale[key]
    const fallbackValue = fallback[key]

    if (isPlainObject(localeValue) && isPlainObject(fallbackValue)) {
      merged[key] = mergeLocaleMessages(fallbackValue, localeValue)
    } else {
      merged[key] = localeValue
    }
  })

  return merged
}

const setI18nLanguage = (lang, app) => {
  app.i18n.locale = lang
  return lang
}

const persistLocale = (app, store, lang) => {
  store.commit('SET_LANG', lang)
  app.$cookies.set(LOCALE_COOKIE_KEY, lang, {
    path: '/',
    maxAge: 60 * 60 * 24 * 365,
  })
  if (process.client) {
    localStorage.setItem(LOCALE_COOKIE_KEY, lang)
  }
}

export default async ({ app, store, route }) => {
  const queryLocale = route?.query?.lang
  const routeLocale = isSupportedLocale(queryLocale) ? normalizeLocale(queryLocale) : ''
  let savedLocale = app.$cookies.get(LOCALE_COOKIE_KEY)
  if (process.client && !savedLocale) {
    savedLocale = localStorage.getItem(LOCALE_COOKIE_KEY)
  }
  const initialLocale = normalizeLocale(routeLocale || savedLocale || store.state.locale || DEFAULT_LOCALE)

  const enMessages = require('~/lang/en.json')
  const messages = {
    en: enMessages,
  }

  if (initialLocale !== DEFAULT_LOCALE) {
    const localeMessages = await import(`~/lang/${initialLocale}.json`)
    messages[initialLocale] = mergeLocaleMessages(
      enMessages,
      localeMessages.default || localeMessages,
    )
    loadedLanguages.push(initialLocale)
  }

  app.i18n = new VueI18n({
    locale: initialLocale,
    fallbackLocale: DEFAULT_LOCALE,
    messages,
  })

  persistLocale(app, store, initialLocale)

  function loadLanguageAsync(lang) {
    const locale = normalizeLocale(lang)

    const applyLocale = () => {
      setI18nLanguage(locale, app)
      persistLocale(app, store, locale)
      // 埋点公共属性：所有语言切换入口统一在此更新
      if (process.client && typeof Vue.prototype.$setProperty === 'function') {
        Vue.prototype.$setProperty('site_language', locale)
      }
      return locale
    }

    if (app.i18n.locale === locale && store.state.locale === locale) {
      return Promise.resolve(applyLocale())
    }

    if (loadedLanguages.includes(locale)) {
      return Promise.resolve(applyLocale())
    }

    return import(`~/lang/${locale}.json`).then((localeModule) => {
      const fallbackMessages = app.i18n.messages[DEFAULT_LOCALE] || enMessages
      app.i18n.setLocaleMessage(
        locale,
        mergeLocaleMessages(fallbackMessages, localeModule.default || localeModule),
      )
      loadedLanguages.push(locale)
      return applyLocale()
    })
  }

  app.$loadLanguageAsync = loadLanguageAsync
  Vue.prototype.$loadLanguageAsync = loadLanguageAsync
}
